import dayjs from 'dayjs';

/**
 * Sample dispatch calendar dataset.
 * N = 8 service providers; M = 4 cover every weekday and weekend with varied slot patterns.
 * Each SP includes `preferredSP` (boolean) for demo preferred-provider logic.
 */

export const SAMPLE_CALENDAR_LOCATION = {
  latitude: 28.567187,
  longitude: -81.744317,
};

/** YYYY-MM-DD from local today through +30 days (31 days). */
function buildDispatchSampleDatePool() {
  const start = dayjs().startOf('day');
  const pool = [];
  for (let i = 0; i < 31; i += 1) {
    pool.push(start.add(i, 'day').format('YYYY-MM-DD'));
  }
  return pool;
}

/** Random subset per provider on each module load; SPs differ independently. */
function randomAvailableDatesForProvider(pool) {
  const picked = pool.filter(() => Math.random() > 0.42);
  if (picked.length < 6) {
    const copy = [...pool];
    for (let i = copy.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      [copy[i], copy[j]] = [copy[j], copy[i]];
    }
    return copy.slice(0, 14).sort();
  }
  return picked.sort();
}

const DISPATCH_SAMPLE_DATE_POOL = buildDispatchSampleDatePool();

const sampleDispatchCalendarServiceProvidersBase = [
  {
    id: '11f12d0cb284ba5e838b0e023771dbd3',
    name: 'Florida Care',
    status: 'ACTIVE',
    preferredSP: true,
    contacts: {
      email: 'floridasp@gmail.com',
      phoneNumber: '+14075550101',
    },
    address: {
      id: '11f12d0cb2a96d45838b0e023771dbd3',
      locationId: '11f12d0cb2872f7a838b0e023771dbd3',
      locationLat: 28.567187,
      locationLng: -81.744317,
    },
    workSchedule: {
      weekdays: {
        morning: true,
        afternoon: false,
        evening: true,
        allDay: false,
      },
      weekends: {
        morning: true,
        afternoon: true,
        evening: true,
        allDay: true,
      },
      holidays: [
        { holidayName: 'Company retreat', holidayDate: '2026-07-04' },
        { holidayName: "New Year's Day", holidayDate: '2026-01-01' },
      ],
    },
  },
  {
    id: '22f12d0cb284ba5e838b0e023771dbd4',
    name: 'Sunshine Nursing',
    status: 'ACTIVE',
    preferredSP: true,
    contacts: {
      email: 'contact@sunshinenursing.example.com',
      phoneNumber: '+14075550102',
    },
    address: {
      id: '22f12d0cb2a96d45838b0e023771dbd4',
      locationId: '22f12d0cb2872f7a838b0e023771dbd4',
      locationLat: 28.538336,
      locationLng: -81.379234,
    },
    workSchedule: {
      weekdays: {
        morning: true,
        afternoon: true,
        evening: false,
        allDay: false,
      },
      weekends: {
        morning: true,
        afternoon: false,
        evening: true,
        allDay: false,
      },
      holidays: [
        { holidayName: 'Independence Day', holidayDate: '2026-07-04' },
      ],
    },
  },
  {
    id: '33f12d0cb284ba5e838b0e023771dbd5',
    name: 'Metro Health Aides',
    status: 'ACTIVE',
    preferredSP: false,
    contacts: {
      email: 'dispatch@metrohealth.example.com',
      phoneNumber: null,
    },
    address: {
      id: '33f12d0cb2a96d45838b0e023771dbd5',
      locationId: '33f12d0cb2872f7a838b0e023771dbd5',
      locationLat: 28.472372,
      locationLng: -81.458839,
    },
    workSchedule: {
      weekdays: {
        morning: false,
        afternoon: true,
        evening: true,
        allDay: false,
      },
      weekends: {
        morning: false,
        afternoon: true,
        evening: true,
        allDay: false,
      },
      holidays: [],
    },
  },
  {
    id: '44f12d0cb284ba5e838b0e023771dbd6',
    name: 'Coastal Companion Care',
    status: 'ACTIVE',
    preferredSP: false,
    contacts: {
      email: 'hello@coastalcompanion.example.com',
      phoneNumber: '+14075550104',
    },
    address: {
      id: '44f12d0cb2a96d45838b0e023771dbd6',
      locationId: '44f12d0cb2872f7a838b0e023771dbd6',
      locationLat: 28.7041,
      locationLng: -81.3311,
    },
    workSchedule: {
      weekdays: {
        morning: true,
        afternoon: false,
        evening: true,
        allDay: false,
      },
      weekends: {
        morning: true,
        afternoon: true,
        evening: false,
        allDay: false,
      },
      holidays: [{ holidayName: 'Thanksgiving', holidayDate: '2026-11-26' }],
    },
  },
  {
    id: '55f12d0cb284ba5e838b0e023771dbd7',
    name: 'Lake County Respite',
    status: 'ACTIVE',
    preferredSP: false,
    contacts: {
      email: 'care@lakecountyrespite.example.com',
      phoneNumber: '+13525550105',
    },
    address: {
      id: '55f12d0cb2a96d45838b0e023771dbd7',
      locationId: '55f12d0cb2872f7a838b0e023771dbd7',
      locationLat: 28.8029,
      locationLng: -81.6444,
    },
    workSchedule: {
      weekdays: {
        morning: true,
        afternoon: true,
        evening: true,
        allDay: false,
      },
      weekends: {
        morning: false,
        afternoon: false,
        evening: true,
        allDay: false,
      },
      holidays: [{ holidayName: 'Memorial Day', holidayDate: '2026-05-25' }],
    },
  },
  {
    id: '66f12d0cb284ba5e838b0e023771dbd8',
    name: 'All Hours Home Support',
    status: 'ACTIVE',
    preferredSP: false,
    contacts: {
      email: 'ops@allhours.example.com',
      phoneNumber: '+14075550106',
    },
    address: {
      id: '66f12d0cb2a96d45838b0e023771dbd8',
      locationId: '66f12d0cb2872f7a838b0e023771dbd8',
      locationLat: 28.60199,
      locationLng: -81.20786,
    },
    workSchedule: {
      weekdays: {
        morning: false,
        afternoon: false,
        evening: false,
        allDay: true,
      },
      weekends: {
        morning: false,
        afternoon: false,
        evening: false,
        allDay: true,
      },
      holidays: [],
    },
  },
  {
    id: '77f12d0cb284ba5e838b0e023771dbd9',
    name: 'Weekday Morning Clinic',
    status: 'ACTIVE',
    preferredSP: false,
    contacts: {
      email: 'morning@weekdayclinic.example.com',
      phoneNumber: '+14075550107',
    },
    address: {
      id: '77f12d0cb2a96d45838b0e023771dbd9',
      locationId: '77f12d0cb2872f7a838b0e023771dbd9',
      locationLat: 28.4158,
      locationLng: -81.298,
    },
    workSchedule: {
      weekdays: {
        morning: true,
        afternoon: false,
        evening: false,
        allDay: false,
      },
      weekends: {
        morning: true,
        afternoon: false,
        evening: false,
        allDay: false,
      },
      holidays: [{ holidayName: 'Labor Day', holidayDate: '2026-09-07' }],
    },
  },
  {
    id: '88f12d0cb284ba5e838b0e023771dbda',
    name: 'Evening & Night Visits LLC',
    status: 'ACTIVE',
    preferredSP: false,
    contacts: {
      email: 'book@eveningnight.example.com',
      phoneNumber: '+14075550108',
    },
    address: {
      id: '88f12d0cb2a96d45838b0e023771dbda',
      locationId: '88f12d0cb2872f7a838b0e023771dbda',
      locationLat: 28.3588,
      locationLng: -81.5597,
    },
    workSchedule: {
      weekdays: {
        morning: false,
        afternoon: false,
        evening: true,
        allDay: false,
      },
      weekends: {
        morning: false,
        afternoon: false,
        evening: true,
        allDay: false,
      },
      holidays: [{ holidayName: 'Christmas Day', holidayDate: '2026-12-25' }],
    },
  },
];

export const sampleDispatchCalendarServiceProviders =
  sampleDispatchCalendarServiceProvidersBase.map(sp => ({
    ...sp,
    availableDates: randomAvailableDatesForProvider(DISPATCH_SAMPLE_DATE_POOL),
  }));
